/**
 * @fileoverview Main Operator Administration Panel for Farmeando Aura.
 * Allows stadium operators to control projections, start/pause/reset rounds,
 * monitor real-time combat telemetry at 100ms, manage themes, and view match history.
 * 
 * @module components/Admin/Games/FarmeandoAura
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { PremiumQR } from './../../Commons/PremiumQR';
import { FarmeandoAuraThemeModal } from './FarmeandoAura/FarmeandoAuraThemeModal';

import {
    GetThemes,
    GetConfig,
    SaveConfig,
    GetMatchesHistory,
    DeleteTheme,
    CloneTheme,
    ShowCover,
    ShowGame,
    StartGame,
    PauseGame,
    ResumeGame,
    FinishEarly,
    ResetGame,
    GetRealtimeState
} from './../../../utilities/BL/FarmeandoAura';

import {
    ConnectWebSocketForFarmeandoAuraAdmin,
    DisconnectWebSocketForFarmeandoAuraAdmin
} from './../../../utilities/WS/Admin/FarmeandoAuraWS';

/**
 * Farmeando Aura Operator Component.
 * 
 * @function FarmeandoAura
 * @param {Object} props - Component properties.
 * @param {Function} props.getOutOfTheDynamic - Callback to exit dynamic back to dashboard.
 * @returns {React.ReactElement} Admin panel markup.
 */
export const FarmeandoAura = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Loading & Refresh states
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
    const [lastRefreshTimestampString, setLastRefreshTimestampString] = useState('');
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('connecting');

    // Themes & Configuration
    const [availableThemesList, setAvailableThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(0);
    const [activeThemeObject, setActiveThemeObject] = useState(null);
    const [roomConfigState, setRoomConfigState] = useState({
        roundDuration: 10,
        gameMode: 'simultaneous'
    });

    // Real-time Game Telemetry State
    const [currentCombatGameState, setCurrentCombatGameState] = useState({
        state: 'idle', // idle, cover, game_ready, countdown, playing, paused, finished
        roundDuration: 10,
        gameMode: 'simultaneous',
        remainingSeconds: 10,
        scoreJ1: 0,
        scoreJ2: 0,
        totalTapsJ1: 0,
        totalTapsJ2: 0,
        activeFansJ1: 0,
        activeFansJ2: 0,
        comboJ1: 0,
        comboJ2: 0,
        transformIndexJ1: 0,
        transformIndexJ2: 0,
        winner: null
    });

    // Countdown active count
    const [countdownSecondsRemaining, setCountdownSecondsRemaining] = useState(null);

    // Match History
    const [matchesHistoryList, setMatchesHistoryList] = useState([]);

    // Modals
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfiguration, setConfirmModalConfiguration] = useState({
        message: '',
        actionToExecute: null,
        yesButtonText: 'Sí, confirmar',
        noButtonText: 'Cancelar'
    });
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEditObject, setThemeToEditObject] = useState(null);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [fanJoinQrUrl, setFanJoinQrUrl] = useState('');
    const [screenTargetDisplayUrl, setScreenTargetDisplayUrl] = useState('');

    /**
     * Resolves media path into an absolute preview URL.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
    };

    /**
     * Handles incoming real-time WebSocket events for the operator.
     */
    const handleWebSocketMessageReceived = useCallback((receivedPacket) => {
        if (!receivedPacket || typeof receivedPacket !== 'object') return;

        const packetType = receivedPacket.type;

        switch (packetType) {
            case 'aura_telemetry':
                if (receivedPacket.data) {
                    const telemetryData = receivedPacket.data;
                    setCurrentCombatGameState((previousState) => ({
                        ...previousState,
                        scoreJ1: telemetryData.scoreJ1 ?? previousState.scoreJ1,
                        scoreJ2: telemetryData.scoreJ2 ?? previousState.scoreJ2,
                        totalTapsJ1: telemetryData.totalTapsJ1 ?? previousState.totalTapsJ1,
                        totalTapsJ2: telemetryData.totalTapsJ2 ?? previousState.totalTapsJ2,
                        activeFansJ1: telemetryData.activeFansJ1 ?? previousState.activeFansJ1,
                        activeFansJ2: telemetryData.activeFansJ2 ?? previousState.activeFansJ2,
                        comboJ1: telemetryData.comboJ1 ?? previousState.comboJ1,
                        comboJ2: telemetryData.comboJ2 ?? previousState.comboJ2,
                        transformIndexJ1: telemetryData.transformIndexJ1 ?? previousState.transformIndexJ1,
                        transformIndexJ2: telemetryData.transformIndexJ2 ?? previousState.transformIndexJ2,
                        remainingSeconds: typeof telemetryData.remainingMs === 'number'
                            ? Math.max(0, Math.round((telemetryData.remainingMs / 1000) * 10) / 10)
                            : previousState.remainingSeconds
                    }));
                }
                break;

            case 'aura_countdown':
                setCountdownSecondsRemaining(receivedPacket.count);
                if (receivedPacket.state) {
                    setCurrentCombatGameState(receivedPacket.state);
                }
                break;

            case 'aura_round_started':
                setCountdownSecondsRemaining(null);
                if (receivedPacket.state) {
                    setCurrentCombatGameState(receivedPacket.state);
                }
                break;

            case 'aura_game_over':
                setCountdownSecondsRemaining(null);
                if (receivedPacket.state) {
                    setCurrentCombatGameState(receivedPacket.state);
                }
                loadMatchesHistory();
                break;

            case 'aura_show_cover':
            case 'aura_show_game':
            case 'aura_paused':
            case 'aura_resumed':
            case 'aura_reset':
            case 'aura_snapshot':
                if (receivedPacket.state) {
                    setCurrentCombatGameState(receivedPacket.state);
                }
                break;

            default:
                break;
        }
    }, []);

    /**
     * Initializes data and WebSocket listeners on component mount.
     */
    useEffect(() => {
        loadAllInitialAdminData();

        ConnectWebSocketForFarmeandoAuraAdmin(
            handleWebSocketMessageReceived,
            (newConnectionStatus) => setWebSocketConnectionStatus(newConnectionStatus)
        );

        return () => {
            DisconnectWebSocketForFarmeandoAuraAdmin();
        };
    }, [handleWebSocketMessageReceived]);

    /**
     * Fetches all themes, room configuration, real-time snapshot and match history.
     */
    const loadAllInitialAdminData = async () => {
        setIsLoadingInitialData(true);
        try {
            // 1. Fetch Themes
            const themesResponse = await GetThemes();
            if (themesResponse.success && Array.isArray(themesResponse.data)) {
                setAvailableThemesList(themesResponse.data);
            }

            // 2. Fetch Room Configuration
            const configResponse = await GetConfig();
            if (configResponse.success && configResponse.data) {
                const configData = configResponse.data;
                setRoomConfigState({
                    roundDuration: configData.roundDuration || 10,
                    gameMode: configData.gameMode || 'simultaneous'
                });

                if (configData.activeThemeId) {
                    setSelectedThemeId(configData.activeThemeId);
                } else if (themesResponse.success && themesResponse.data?.length > 0) {
                    setSelectedThemeId(themesResponse.data[0].id);
                }

                if (configData.activeTheme) {
                    setActiveThemeObject(configData.activeTheme);
                }
            }

            // 3. Fetch Real-time Engine Snapshot
            const realtimeResponse = await GetRealtimeState();
            if (realtimeResponse.success && realtimeResponse.data) {
                setCurrentCombatGameState(realtimeResponse.data);
                if (realtimeResponse.data.currentTheme) {
                    setActiveThemeObject(realtimeResponse.data.currentTheme);
                }
            }

            // 4. Fetch History
            await loadMatchesHistory();
        } catch (initializationError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar los datos de administración.' });
        } finally {
            setIsLoadingInitialData(false);
            setLastRefreshTimestampString(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            );
        }
    };

    /**
     * Loads the match history log from database.
     */
    const loadMatchesHistory = async () => {
        try {
            const historyResponse = await GetMatchesHistory(25);
            if (historyResponse.success && Array.isArray(historyResponse.data)) {
                setMatchesHistoryList(historyResponse.data);
            }
        } catch (historyException) {
            // Non-blocking log fetch
        }
    };

    /**
     * Validates whether a theme is actively selected before proceeding with game or projection actions.
     * 
     * @function checkThemeSelected
     * @returns {boolean} True if a theme is selected, false otherwise.
     */
    const checkThemeSelected = () => {
        if (!selectedThemeId || Number(selectedThemeId) === 0 || selectedThemeId === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema para continuar' });
            return false;
        }
        return true;
    };

    /**
     * Updates the active theme in room config and persists.
     */
    const handleActiveThemeSelectionChange = async (event) => {
        const rawValue = event.target.value;
        const newlySelectedId = rawValue ? Number(rawValue) : 0;
        setSelectedThemeId(newlySelectedId);

        const foundTheme = availableThemesList.find((themeItem) => themeItem.id === newlySelectedId);
        setActiveThemeObject(foundTheme || null);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando tema activo...' });
        const saveConfigResponse = await SaveConfig({
            activeThemeId: newlySelectedId > 0 ? newlySelectedId : null,
            roundDuration: roomConfigState.roundDuration,
            gameMode: roomConfigState.gameMode
        });

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema activo actualizado correctamente.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al actualizar el tema activo.' });
        }
    };

    /**
     * Updates round duration or game mode and persists.
     */
    const handleConfigParameterChange = async (parameterName, parameterValue) => {
        const updatedConfig = {
            ...roomConfigState,
            [parameterName]: parameterValue
        };
        setRoomConfigState(updatedConfig);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración...' });
        const saveConfigResponse = await SaveConfig({
            activeThemeId: selectedThemeId > 0 ? selectedThemeId : null,
            roundDuration: updatedConfig.roundDuration,
            gameMode: updatedConfig.gameMode
        });

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración guardada.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al guardar la configuración.' });
        }
    };

    // ==========================================
    // PROJECTION CONTROLS
    // ==========================================

    const handleShowCoverAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Portada Promocional...' });
        const coverResponse = await ShowCover();
        if (coverResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada emitida a pantalla gigante.' });
            if (coverResponse.state) setCurrentCombatGameState(coverResponse.state);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: coverResponse.message || 'Error al emitir portada.' });
        }
    };

    const handleShowGameAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Escenario de Combate...' });
        const gameResponse = await ShowGame();
        if (gameResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Escenario de combate proyectado en pantalla.' });
            if (gameResponse.state) setCurrentCombatGameState(gameResponse.state);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: gameResponse.message || 'Error al proyectar escenario.' });
        }
    };

    const handleOpenQrModal = () => {
        if (!checkThemeSelected()) return;
        const currentHostname = window.location.hostname;
        const currentPort = window.location.port ? `:${window.location.port}` : '';
        const currentProtocol = window.location.protocol;

        const fanUrl = `${currentProtocol}//${currentHostname}${currentPort}/farmeando-aura`;
        const screenUrl = `${currentProtocol}//${currentHostname}${currentPort}/screen`;

        setFanJoinQrUrl(fanUrl);
        setScreenTargetDisplayUrl(screenUrl);
        setIsQrModalOpen(true);
    };

    // ==========================================
    // MATCH CONTROLS
    // ==========================================

    const handleStartGameAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciando combate con cuenta regresiva...' });
        const startResponse = await StartGame(roomConfigState.roundDuration, roomConfigState.gameMode);
        if (startResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Partida en curso!' });
            if (startResponse.state) setCurrentCombatGameState(startResponse.state);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse.message || 'Error al iniciar partida.' });
        }
    };

    const handleTogglePauseAction = async () => {
        if (currentCombatGameState.state === 'playing') {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Pausando combate...' });
            const pauseResponse = await PauseGame();
            if (pauseResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Combate pausado.' });
                if (pauseResponse.state) setCurrentCombatGameState(pauseResponse.state);
            }
        } else if (currentCombatGameState.state === 'paused') {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reanudando combate...' });
            const resumeResponse = await ResumeGame();
            if (resumeResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Combate reanudado.' });
                if (resumeResponse.state) setCurrentCombatGameState(resumeResponse.state);
            }
        }
    };

    const handleConfirmFinishEarly = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de finalizar la partida antes de tiempo? Se declarará ganador de inmediato con el puntaje actual.',
            yesButtonText: 'Sí, finalizar ahora',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Deteniendo partida de emergencia...' });
                const finishResponse = await FinishEarly();
                if (finishResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida concluida anticipadamente.' });
                    if (finishResponse.state) setCurrentCombatGameState(finishResponse.state);
                    await loadMatchesHistory();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: finishResponse.message || 'Error al finalizar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleConfirmResetGame = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de reiniciar la sala a estado inicial? Se borrarán los contadores de la ronda activa.',
            yesButtonText: 'Sí, reiniciar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando sala...' });
                const resetResponse = await ResetGame();
                if (resetResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Sala reiniciada a estado de espera.' });
                    if (resetResponse.state) setCurrentCombatGameState(resetResponse.state);
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message || 'Error al reiniciar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // ==========================================
    // THEME CRUD ACTIONS
    // ==========================================

    const handleOpenCreateThemeModal = () => {
        setThemeToEditObject(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        const foundTheme = availableThemesList.find((item) => item.id === selectedThemeId);
        setThemeToEditObject(foundTheme || null);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando tema...' });
        try {
            const cloneResponse = await CloneTheme(selectedThemeId);
            if (cloneResponse.success && cloneResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema clonado exitosamente.' });
                await loadAllInitialAdminData();
                setSelectedThemeId(cloneResponse.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse.message || 'Error al clonar tema.' });
            }
        } catch (cloneError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar tema.' });
        }
    };

    const handleConfirmDeleteTheme = () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        const currentTheme = availableThemesList.find((t) => t.id === selectedThemeId);
        const themeName = currentTheme ? currentTheme.name : 'este tema';

        setConfirmModalConfiguration({
            message: `¿Estás seguro de que deseas eliminar permanentemente el tema "${themeName}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando tema...' });
                const deleteResponse = await DeleteTheme(selectedThemeId);
                if (deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                    await loadAllInitialAdminData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse.message || 'Error al eliminar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    /**
     * Renders a human-readable game state badge.
     */
    const renderGameStateBadge = () => {
        const gameStateKey = currentCombatGameState.state;

        switch (gameStateKey) {
            case 'idle':
                return <span className="badge bg-secondary fs-6 px-3 py-2">En Espera (Idle)</span>;
            case 'cover':
                return <span className="badge bg-info text-dark fs-6 px-3 py-2">🖼️ Proyectando Portada</span>;
            case 'game_ready':
                return <span className="badge bg-primary fs-6 px-3 py-2">🏟️ Escenario Listo</span>;
            case 'countdown':
                return (
                    <span className="badge bg-warning text-dark fs-6 px-3 py-2 animate-pulse">
                        ⏳ Cuenta Regresiva: {countdownSecondsRemaining ?? 3}s
                    </span>
                );
            case 'playing':
                return <span className="badge bg-danger fs-6 px-3 py-2 animate-pulse">🔥 ¡COMBATE EN CURSO!</span>;
            case 'paused':
                return <span className="badge bg-warning text-dark fs-6 px-3 py-2">⏸️ Partida Pausada</span>;
            case 'finished':
                return (
                    <span className="badge bg-success fs-6 px-3 py-2">
                        🏆 ¡K.O. - Finalizado! (Ganador: {currentCombatGameState.winner || 'Empate'})
                    </span>
                );
            default:
                return <span className="badge bg-secondary fs-6 px-3 py-2">{gameStateKey}</span>;
        }
    };

    // Calculate health/aura progress bar percentages
    const maxScore = Math.max(1, currentCombatGameState.scoreJ1 + currentCombatGameState.scoreJ2);
    const percentageJ1 = Math.round((currentCombatGameState.scoreJ1 / maxScore) * 100);
    const percentageJ2 = Math.round((currentCombatGameState.scoreJ2 / maxScore) * 100);

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            {/* Modal de Confirmación */}
            {isConfirmModalOpen && (
                <Confirm
                    message={confirmModalConfiguration.message}
                    actionToYesButton={confirmModalConfiguration.actionToExecute}
                    textYesButton={confirmModalConfiguration.yesButtonText}
                    textNoButton={confirmModalConfiguration.noButtonText}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Modal de Códigos QR y Enlaces */}
            {isQrModalOpen && (
                <>
                    <div className="cModal__Background" onClick={() => setIsQrModalOpen(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div
                            className="cModal__Container__Content position-relative p-0 overflow-hidden"
                            style={{ background: 'transparent', boxShadow: 'none', maxWidth: '420px' }}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-dark position-absolute top-0 end-0 m-3 rounded-circle p-2"
                                onClick={() => setIsQrModalOpen(false)}
                                style={{ zIndex: 20 }}
                            >
                                <i className="bi bi-x-lg text-white"></i>
                            </button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1">
                                    <i className="bi bi-tv me-1"></i> Pantalla Gigante Estadio / Overlay:
                                </span>
                                <a
                                    href={screenTargetDisplayUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fw-bold text-info small text-break text-decoration-none d-block"
                                >
                                    {screenTargetDisplayUrl} <i className="bi bi-box-arrow-up-right ms-1"></i>
                                </a>
                            </div>
                            <PremiumQR url={fanJoinQrUrl} title="FARMEANDO AURA" />
                        </div>
                    </div>
                </>
            )}

            {/* Modal de Gestión de Temas */}
            {isThemeModalOpen && (
                <FarmeandoAuraThemeModal
                    isOpen={isThemeModalOpen}
                    initialThemeData={themeToEditObject}
                    onCloseModal={() => setIsThemeModalOpen(false)}
                    onThemeSavedSuccessfully={(savedTheme) => {
                        loadAllInitialAdminData();
                        if (savedTheme?.id) setSelectedThemeId(savedTheme.id);
                    }}
                />
            )}

            {/* HEADER DEL PANEL */}
            <div className="mb-4 text-center text-dark">
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <i className="bi bi-lightning-charge-fill text-warning fs-1"></i>
                    <h2 className="text-uppercase fw-bold m-0" style={{ letterSpacing: '2px' }}>
                        Farmeando Aura
                    </h2>
                </div>
                <div className="d-inline-flex align-items-center gap-3 bg-white px-3 py-1 rounded-pill border shadow-sm mt-2 flex-wrap">
                    <span className="small">
                        {webSocketConnectionStatus === 'connected' && <strong className="text-success">🟢 En Línea</strong>}
                        {webSocketConnectionStatus === 'connecting' && <strong className="text-warning">🟡 Conectando...</strong>}
                        {webSocketConnectionStatus === 'reconnecting' && <strong className="text-warning">🟡 Reconectando...</strong>}
                        {webSocketConnectionStatus === 'error' && <strong className="text-danger">🔴 Desconectado</strong>}
                    </span>
                    <span className="small text-muted border-start ps-3">
                        Actualizado: <strong className="text-dark">{lastRefreshTimestampString}</strong>
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-light border rounded-circle p-1 d-flex align-items-center justify-content-center"
                        onClick={loadAllInitialAdminData}
                        title="Refrescar estado"
                        style={{ width: '28px', height: '28px' }}
                    >
                        <i className={`bi bi-arrow-repeat text-primary ${isLoadingInitialData ? 'spinner-border spinner-border-sm border-0' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 1: TARJETAS DE PROYECCIÓN Y CONTROL DE PARTIDA         */}
            {/* ============================================================ */}
            <div className="row g-4 mb-4">
                {/* TARJETA 1: PROYECCIÓN Y ENLACES */}
                <div className="col-lg-5">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-projector-fill text-warning me-2"></i> 1. Proyección en Estadio
                            </h5>
                            <span className="badge bg-primary">Vistas</span>
                        </div>
                        <div className="card-body bg-light d-flex flex-column justify-content-between p-4">
                            <p className="small text-muted mb-3">
                                Alterna instantáneamente lo que se proyecta en la pantalla gigante (/screen) entre la Portada y el Escenario de Combate.
                            </p>
                            <div className="d-grid gap-3">
                                <button
                                    type="button"
                                    className="btn btn-info fw-bold py-2 text-dark shadow-sm"
                                    onClick={handleShowCoverAction}
                                >
                                    <i className="bi bi-image me-1"></i> Mostrar portada
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary fw-bold py-2 text-white shadow-sm"
                                    onClick={handleShowGameAction}
                                >
                                    <i className="bi bi-tv-fill me-1"></i> Proyectar pantalla de juego
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-dark fw-bold py-2 shadow-sm"
                                    onClick={handleOpenQrModal}
                                >
                                    <i className="bi bi-qr-code-scan me-1"></i> Ver instrucciones de conexión
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TARJETA 2: CONTROL DE PARTIDA */}
                <div className="col-lg-7">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-controller text-danger me-2"></i> 2. Control de Partida
                            </h5>
                            {renderGameStateBadge()}
                        </div>
                        <div className="card-body bg-light p-4">
                            {/* Selector de Tema Activo */}
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-muted">
                                    <i className="bi bi-palette-fill me-1 text-primary"></i> Tema Activo:
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                    <select
                                        className="form-select fw-bold border-primary shadow-sm"
                                        value={selectedThemeId || ''}
                                        onChange={handleActiveThemeSelectionChange}
                                        disabled={currentCombatGameState.state === 'playing' || currentCombatGameState.state === 'countdown'}
                                    >
                                        <option value="">-- Selecciona un tema --</option>
                                        {availableThemesList.map((themeOption) => (
                                            <option key={themeOption.id} value={themeOption.id}>
                                                {themeOption.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary fw-bold text-nowrap"
                                        onClick={handleOpenEditThemeModal}
                                        disabled={!selectedThemeId || selectedThemeId <= 0}
                                        title="Editar tema seleccionado"
                                    >
                                        <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold text-nowrap"
                                        onClick={handleOpenCreateThemeModal}
                                        title="Crear nuevo tema"
                                    >
                                        <i className="bi bi-plus-lg"></i>
                                    </button>
                                </div>
                            </div>

                            {/* Selectores de duración y modo */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-muted">Duración de la Ronda:</label>
                                    <select
                                        className="form-select fw-bold"
                                        value={roomConfigState.roundDuration}
                                        onChange={(e) => handleConfigParameterChange('roundDuration', Number(e.target.value))}
                                        disabled={currentCombatGameState.state === 'playing' || currentCombatGameState.state === 'countdown'}
                                    >
                                        <option value={5}>5 Segundos (Flash)</option>
                                        <option value={10}>10 Segundos (Recomendado)</option>
                                        <option value={15}>15 Segundos</option>
                                        <option value={20}>20 Segundos</option>
                                        <option value={30}>30 Segundos</option>
                                        <option value={60}>60 Segundos</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-muted">Modo de Juego:</label>
                                    <select
                                        className="form-select fw-bold"
                                        value={roomConfigState.gameMode}
                                        onChange={(e) => handleConfigParameterChange('gameMode', e.target.value)}
                                        disabled={currentCombatGameState.state === 'playing' || currentCombatGameState.state === 'countdown'}
                                    >
                                        <option value="simultaneous">Simultáneo (1 vs 1 / Afición vs Afición)</option>
                                        <option value="turns">Por Turnos (J1 y luego J2)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Botones de acción de juego */}
                            <div className="row g-2">
                                <div className="col-md-6">
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold w-100 py-2 shadow-sm text-white"
                                        onClick={handleStartGameAction}
                                        disabled={
                                            currentCombatGameState.state === 'playing' ||
                                            currentCombatGameState.state === 'countdown'
                                        }
                                    >
                                        <i className="bi bi-play-fill fs-5 me-1"></i> Iniciar Partida (3, 2, 1)
                                    </button>
                                </div>
                                <div className="col-md-6">
                                    <button
                                        type="button"
                                        className="btn btn-warning fw-bold w-100 py-2 shadow-sm text-dark"
                                        onClick={handleTogglePauseAction}
                                        disabled={
                                            currentCombatGameState.state !== 'playing' &&
                                            currentCombatGameState.state !== 'paused'
                                        }
                                    >
                                        {currentCombatGameState.state === 'paused' ? (
                                            <>
                                                <i className="bi bi-play-circle-fill me-1"></i> Reanudar Partida
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-pause-fill me-1"></i> Pausar Partida
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="col-md-6 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger fw-bold w-100 py-2 shadow-sm"
                                        onClick={handleConfirmFinishEarly}
                                        disabled={
                                            currentCombatGameState.state !== 'playing' &&
                                            currentCombatGameState.state !== 'paused'
                                        }
                                    >
                                        <i className="bi bi-stop-circle-fill me-1"></i> Finalizar Antes de Tiempo
                                    </button>
                                </div>
                                <div className="col-md-6 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary fw-bold w-100 py-2 shadow-sm"
                                        onClick={handleConfirmResetGame}
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-1"></i> Reiniciar Sala (Reset)
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 2: MONITOR DE COMBATE EN VIVO (TELEMETRÍA A 100MS)     */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-activity text-success me-2"></i> 3. Monitor de Combate en Vivo (Telemetría a 100ms)
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-secondary">
                            Fans Conectados:{' '}
                            <strong className="text-warning">
                                {currentCombatGameState.activeFansJ1 + currentCombatGameState.activeFansJ2}
                            </strong>
                        </span>
                    </div>
                </div>
                <div className="card-body bg-white p-4">
                    {/* Timer Central y Estado */}
                    <div className="text-center mb-4">
                        <span className="small text-uppercase fw-bold text-muted d-block">Tiempo Restante</span>
                        <h1 className="fw-bold display-4 text-dark m-0 font-monospace">
                            {currentCombatGameState.remainingSeconds?.toFixed(1)}s
                        </h1>
                        <div className="progress mt-2 mx-auto" style={{ height: '8px', maxWidth: '400px' }}>
                            <div
                                className="progress-bar bg-warning progress-bar-striped progress-bar-animated"
                                style={{
                                    width: `${Math.min(
                                        100,
                                        (currentCombatGameState.remainingSeconds / (roomConfigState.roundDuration || 10)) * 100
                                    )}%`
                                }}
                            ></div>
                        </div>
                    </div>

                    {/* Comparativa J1 vs J2 */}
                    <div className="row align-items-center g-3">
                        {/* LUCHADOR JUGADOR 1 */}
                        <div className="col-md-5">
                            <div
                                className="p-3 rounded-4 border shadow-sm h-100"
                                style={{
                                    borderLeft: `8px solid ${activeThemeObject?.player1Color || '#00ffcc'}`,
                                    background: '#f8fafc'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h5 className="fw-bold m-0" style={{ color: activeThemeObject?.player1Color || '#00a884' }}>
                                        <i className="bi bi-shield-shaded me-1"></i> Jugador 1 
                                    </h5>
                                    <span className="badge bg-dark">
                                        {currentCombatGameState.activeFansJ1} aficionado(s)
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-3 my-2">
                                    <img
                                        src={resolveMediaUrl(activeThemeObject?.player1Avatar || '/assets/FarmeandoAura/avatars/J1I.png')}
                                        alt="J1 Avatar"
                                        className="rounded-circle border p-1 bg-white"
                                        style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <div>
                                        <span className="small text-muted d-block">Puntaje Promedio:</span>
                                        <h3 className="fw-bold m-0 text-dark">
                                            {currentCombatGameState.scoreJ1?.toFixed(2)} pts
                                        </h3>
                                        <span className="small text-secondary">
                                            Taps Totales: <strong>{currentCombatGameState.totalTapsJ1}</strong>
                                        </span>
                                    </div>
                                </div>
                                <div className="progress mt-2" style={{ height: '14px' }}>
                                    <div
                                        className="progress-bar fw-bold"
                                        style={{
                                            width: `${percentageJ1}%`,
                                            backgroundColor: activeThemeObject?.player1Color || '#00ffcc'
                                        }}
                                    >
                                        {percentageJ1 > 15 && `${percentageJ1}%`}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* VS CENTRAL */}
                        <div className="col-md-2 text-center">
                            <span className="badge bg-dark fs-4 px-3 py-2 rounded-pill shadow">VS</span>
                        </div>

                        {/* LUCHADOR JUGADOR 2 */}
                        <div className="col-md-5">
                            <div
                                className="p-3 rounded-4 border shadow-sm h-100"
                                style={{
                                    borderRight: `8px solid ${activeThemeObject?.player2Color || '#ff0055'}`,
                                    background: '#f8fafc'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="badge bg-dark">
                                        {currentCombatGameState.activeFansJ2} aficionado(s)
                                    </span>
                                    <h5 className="fw-bold m-0" style={{ color: activeThemeObject?.player2Color || '#d90044' }}>
                                        Jugador 2 <i className="bi bi-shield-shaded ms-1"></i>
                                    </h5>
                                </div>
                                <div className="d-flex align-items-center justify-content-end gap-3 my-2 text-end">
                                    <div>
                                        <span className="small text-muted d-block">Puntaje Promedio:</span>
                                        <h3 className="fw-bold m-0 text-dark">
                                            {currentCombatGameState.scoreJ2?.toFixed(2)} pts
                                        </h3>
                                        <span className="small text-secondary">
                                            Taps Totales: <strong>{currentCombatGameState.totalTapsJ2}</strong>
                                        </span>
                                    </div>
                                    <img
                                        src={resolveMediaUrl(activeThemeObject?.player2Avatar || '/assets/FarmeandoAura/avatars/J2I.png')}
                                        alt="J2 Avatar"
                                        className="rounded-circle border p-1 bg-white"
                                        style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                                <div className="progress mt-2" style={{ height: '14px' }}>
                                    <div
                                        className="progress-bar fw-bold ms-auto"
                                        style={{
                                            width: `${percentageJ2}%`,
                                            backgroundColor: activeThemeObject?.player2Color || '#ff0055'
                                        }}
                                    >
                                        {percentageJ2 > 15 && `${percentageJ2}%`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 3: GESTOR DE TEMAS Y PERSONALIZACIÓN                   */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-palette-fill text-info me-2"></i> 4. Gestor de temas
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-success fw-bold"
                        onClick={handleOpenCreateThemeModal}
                    >
                        <i className="bi bi-plus-circle me-1"></i> Crear nuevo tema
                    </button>
                </div>
                <div className="card-body bg-light p-4">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Temas:</label>
                            <select
                                className="form-select fw-bold border-primary shadow-sm"
                                value={selectedThemeId || ''}
                                onChange={handleActiveThemeSelectionChange}
                            >
                                <option value="">-- Selecciona un tema --</option>
                                {availableThemesList.map((themeItem) => (
                                    <option key={themeItem.id} value={themeItem.id}>
                                        {themeItem.name} {themeItem.id === selectedThemeId ? '⭐ (Activo)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6 d-flex gap-2 justify-content-md-end flex-wrap pt-md-4">
                            <button
                                type="button"
                                className="btn btn-primary fw-bold shadow-sm"
                                onClick={handleOpenEditThemeModal}
                                disabled={!selectedThemeId || selectedThemeId <= 0}
                            >
                                <i className="bi bi-pencil-square me-1"></i> Editar Tema
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold shadow-sm"
                                onClick={handleCloneSelectedTheme}
                                disabled={!selectedThemeId || selectedThemeId <= 0}
                            >
                                <i className="bi bi-copy me-1"></i> Clonar
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-danger fw-bold shadow-sm"
                                onClick={handleConfirmDeleteTheme}
                                disabled={!selectedThemeId || selectedThemeId <= 0 || availableThemesList.length <= 1}
                            >
                                <i className="bi bi-trash-fill me-1"></i> Eliminar
                            </button>
                        </div>
                    </div>

                    {/* Previsualización del Tema Activo */}
                    {activeThemeObject && (
                        <div className="mt-3 p-3 bg-white rounded-3 border shadow-sm">
                            <div className="row g-2 align-items-center">
                                <div className="col-auto">
                                    {activeThemeObject.brandLogoUrl && (
                                        <img
                                            src={resolveMediaUrl(activeThemeObject.brandLogoUrl)}
                                            alt="Logo Brand"
                                            style={{ maxHeight: '40px' }}
                                        />
                                    )}
                                    {activeThemeObject.sponsorLogoUrl && (
                                        <img
                                            src={resolveMediaUrl(activeThemeObject.sponsorLogoUrl)}
                                            alt="Logo Sponsor"
                                            className="ms-2"
                                            style={{ maxHeight: '40px' }}
                                        />
                                    )}
                                </div>
                                <div className="col">
                                    <strong className="text-dark">{activeThemeObject.name}</strong>
                                    <div className="small text-muted">
                                        Portada: {activeThemeObject.coverTitle || 'FARMEANDO AURA'} | Colores:{' '}
                                        <span className="badge" style={{ backgroundColor: activeThemeObject.player1Color }}>
                                            J1
                                        </span>{' '}
                                        <span className="badge" style={{ backgroundColor: activeThemeObject.player2Color }}>
                                            J2
                                        </span>{' '}
                                        | Baraja: {activeThemeObject.deckImages?.length || 0} slots
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 4: HISTORIAL DE PARTIDAS                               */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-clock-history text-secondary me-2"></i> 5. Historial de Partidas
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={loadMatchesHistory}
                    >
                        <i className="bi bi-arrow-repeat me-1"></i> Actualizar Historial
                    </button>
                </div>
                <div className="card-body bg-white p-0">
                    <div className="table-responsive">
                        <table className="table table-striped table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th># Partida</th>
                                    <th>Fecha / Hora</th>
                                    <th>Modo</th>
                                    <th>Ganador</th>
                                    <th>Puntaje J1</th>
                                    <th>Puntaje J2</th>
                                    <th>Taps Totales</th>
                                    <th>Fans Activos</th>
                                    <th>Duración</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matchesHistoryList.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-muted">
                                            No se han jugado partidas en esta sala todavía.
                                        </td>
                                    </tr>
                                ) : (
                                    matchesHistoryList.map((matchItem) => (
                                        <tr key={matchItem.id}>
                                            <td className="fw-bold font-monospace">
                                                {matchItem.matchId?.substring(0, 8) || matchItem.id}
                                            </td>
                                            <td className="small text-muted">
                                                {new Date(matchItem.playedAt).toLocaleString()}
                                            </td>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {matchItem.gameMode === 'turns' ? 'Por Turnos' : 'Simultáneo'}
                                                </span>
                                            </td>
                                            <td>
                                                {matchItem.winner === 'J1' && (
                                                    <span className="badge bg-info text-dark fw-bold">Jugador 1 🏆</span>
                                                )}
                                                {matchItem.winner === 'J2' && (
                                                    <span className="badge bg-danger fw-bold">Jugador 2 🏆</span>
                                                )}
                                                {matchItem.winner !== 'J1' && matchItem.winner !== 'J2' && (
                                                    <span className="badge bg-warning text-dark fw-bold">Empate</span>
                                                )}
                                            </td>
                                            <td className="fw-bold text-info">
                                                {matchItem.scoreJ1?.toFixed(2)} pts
                                            </td>
                                            <td className="fw-bold text-danger">
                                                {matchItem.scoreJ2?.toFixed(2)} pts
                                            </td>
                                            <td className="small">
                                                J1: {matchItem.totalTapsJ1} | J2: {matchItem.totalTapsJ2}
                                            </td>
                                            <td className="small">
                                                J1: {matchItem.activeFansJ1} | J2: {matchItem.activeFansJ2}
                                            </td>
                                            <td className="small font-monospace">
                                                {matchItem.effectiveDurationSeconds}s
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};
